import { Typography, Button } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { Hazard } from 'features/hazards/hazard.types';
import { GeoEventMetadataText } from 'features/geoEvents/components/GeoEventMetadataRows';
import { getDateFormatted, getTimeAgo } from 'shared/dates';
import { ENV } from 'shared/ENV';
import { Helmet } from 'react-helmet';
import {
  MapEntityDetailsLayout,
  useMapEntityDetailsTabs,
} from 'features/mapping/components/MapEntityDetailsLayout';
import { useGeoEvent } from 'features/geoEvents/hooks/useGeoEvent';
import { InlineStatus } from 'components/InlineStatus';
import { useShareOrCopy } from 'hooks/useShareOrCopy';
import { useTabs } from 'hooks/useTabs';
import { GeoEventReports } from 'features/reports/components/GeoEventReports';
import { HazardStructuredInfo } from 'features/hazards/components/HazardStructuredInfo';
import { useSelectedIncidentGeoEventId } from 'features/geoEvents/hooks/useSelectedIncidentGeoEventId';

const TAB_PANEL_ID_PREFIX = 'hazard-details';

const BASE_TABS_TOP = 120;

export const HazardDetails = (): JSX.Element => {
  const geoEventId = useSelectedIncidentGeoEventId();
  const {
    geoEvent: hazard,
    isGeoEventLoading,
    isGeoEventFetchError,
  } = useGeoEvent<Hazard>({
    id: geoEventId,
    useCacheBuster: true,
  });
  const { t } = useTranslation();
  const { canShareOrCopy, shareOrCopyUrl } = useShareOrCopy();
  const { onChangeTab } = useMapEntityDetailsTabs({ baseTop: BASE_TABS_TOP });

  const handleShare = (): void => {
    if (!hazard) {
      return;
    }

    shareOrCopyUrl({
      title: hazard.name,
      text: hazard.name,
      url: `${ENV.VITE_BASE_SHARE_URL}i/${hazard.id}`,
      androidDialogTitle: t('hazard.shareHazard'),
    });
  };

  const tabData = [
    {
      tabId: 'updates',
      label: t('geoEvent.tabs.updates'),
      enabled: true,
      content: hazard && (
        <GeoEventReports geoEventId={hazard.id} geoEventType="hazard" />
      ),
    },
    {
      tabId: 'info',
      label: t('geoEvent.tabs.info'),
      enabled: true,
      content: hazard && <HazardStructuredInfo hazard={hazard} />,
    },
  ];

  const { tabsProps, tabProps, tabPanels } = useTabs({
    idPrefix: TAB_PANEL_ID_PREFIX,
    ariaLabel: t('geoEvent.tabsLabel'),
    tabData,
    onChangeTab,
  });

  if (isGeoEventLoading || isGeoEventFetchError || !hazard) {
    return <InlineStatus status={isGeoEventLoading ? 'loading' : 'error'} />;
  }

  return (
    <>
      <Helmet>
        <title>{t('home.hazardTitle', { name: hazard.name })}</title>
      </Helmet>

      <MapEntityDetailsLayout.Root>
        <MapEntityDetailsLayout.Header
          title={hazard.name}
          subtitle={hazard.address}
        />

        <MapEntityDetailsLayout.Body>
          <MapEntityDetailsLayout.Meta>
            <Typography variant="body2">
              <Trans
                i18nKey="hazard.updated"
                values={{ timeAgo: getTimeAgo(hazard.dateModified) }}
              >
                Updated <b>5 minutes ago</b>
              </Trans>
            </Typography>

            <GeoEventMetadataText>
              <Trans
                i18nKey="hazard.createdBy"
                values={{
                  name:
                    hazard.createdByName.displayNameShort ||
                    t('hazard.watchDuty'),
                  date: getDateFormatted(hazard.dateCreated),
                }}
              >
                Created by <b>Watch Duty</b> · Aug 4, 12:54 PM
              </Trans>
            </GeoEventMetadataText>
          </MapEntityDetailsLayout.Meta>

          {canShareOrCopy && (
            <MapEntityDetailsLayout.Actions>
              <Button
                fullWidth
                color="neutral"
                variant="outlined"
                size="large"
                onClick={handleShare}
              >
                {t('hazard.buttons.share')}
              </Button>
            </MapEntityDetailsLayout.Actions>
          )}
        </MapEntityDetailsLayout.Body>

        <MapEntityDetailsLayout.Tabs
          tabsProps={tabsProps}
          tabProps={tabProps}
          tabPanels={tabPanels}
        />
      </MapEntityDetailsLayout.Root>
    </>
  );
};
